import Cookies from 'js-cookie';
import { env } from '@/utils/env';

/**
 * 注意：这是Google翻译插件设置的Cookie名称，不能随意更改
 * 这是Google翻译读取和设置的Cookie，会设置在根域下（.zeelool.com），会穿透多站点（不合符要求）
 */
const GOOGLE_TRANS_COOKIE = 'googtrans';

/**
 * 同步GoogleTrans，但是不会穿透多站点。项目使用此Cookie判断真实Locale（Google翻译的目标语言）
 */
const NEXT_LOCALE_COOKIE = 'next_locale';

const LOCALE_TO_LANGUAGE_TEXT: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fi: 'Suomi',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederland',
  pt: 'Português',
};

function getDefaultPlatformLanguage() {
  return LOCALE_TO_LANGUAGE_TEXT[env.NEXT_PUBLIC_SITE_LANG] || env.NEXT_PUBLIC_SITE_LANG;
}

/**
 * @desc 获取 RootHost 的信息，主要给设置 Cookie 用的
 * @example 只适合简单 www.zeelool.com 域名，不支持 a.b.example.com 这种复杂的域名
 * @return {string} - '.zeelool.com'
 */
function getRootHost() {
  if (env.NEXT_PUBLIC_ENV === 'local') return location.hostname;
  return `.${location.hostname.split('.').slice(-2).join('.')}`;
}

/**
 * 只暴露 NextLocale，不暴露GoogleTrans
 * 首次需要同步 NEXT_LOCALE_COOKIE 至 GOOGLE_TRANS_COOKIE
 */
export function getNextLocaleCookie() {
  return Cookies.get(NEXT_LOCALE_COOKIE);
}

export function getPlatformLanguage() {
  if (typeof window === 'undefined') {
    return getDefaultPlatformLanguage();
  }

  try {
    const savedLanguage = window.localStorage?.getItem('zl-language');
    if (savedLanguage) {
      return savedLanguage;
    }

    const locale =
      getNextLocaleCookie() ||
      window.localStorage?.getItem('zl-locale') ||
      env.NEXT_PUBLIC_SITE_LANG;

    return LOCALE_TO_LANGUAGE_TEXT[locale] || getDefaultPlatformLanguage();
  } catch {
    return getDefaultPlatformLanguage();
  }
}

/**
 * 首次同步 NEXT_LOCALE_COOKIE 至 GOOGLE_TRANS_COOKIE
 * 防止 GOOGLE_TRANS_COOKIE 穿透或者不同步的情况出现
 */
export function flushLocaleAndGoogleTransCookie() {
  const locale = getNextLocaleCookie();

  if (locale) {
    if (locale === env.NEXT_PUBLIC_SITE_LANG) {
      // locale 和当前站点的默认语言一致，不需要翻译，清空 GOOGLE_TRANS_COOKIE
      delGoogleTransCookie();
    } else {
      // locale 和当前站点的默认语言不一致，需要翻译，强制同步 GOOGLE_TRANS_COOKIE
      setGoogleTransCookie(locale);
    }
  } else {
    // locale 不存在，不需要翻译，清空 GOOGLE_TRANS_COOKIE
    delGoogleTransCookie();
  }
}

/**
 * 和GoogleTrans同步操作
 */
export function setNextLocaleCookie(targetLocale: string) {
  Cookies.set(NEXT_LOCALE_COOKIE, targetLocale);
}

export function setGoogleTransCookie(targetLocale: string, sourceLocale = 'en') {
  const googleTransCookieValue = `/${sourceLocale}/${targetLocale}`;
  setNextLocaleCookie(targetLocale);
  Cookies.set(GOOGLE_TRANS_COOKIE, googleTransCookieValue);
  Cookies.set(GOOGLE_TRANS_COOKIE, googleTransCookieValue, { domain: getRootHost(), path: '/' });
}

/**
 * 和GoogleTrans同步操作，不对外暴露
 */
function delNextLocaleCookie() {
  Cookies.remove(NEXT_LOCALE_COOKIE);
}

export function delGoogleTransCookie() {
  delNextLocaleCookie();
  Cookies.remove(GOOGLE_TRANS_COOKIE);
  Cookies.remove(GOOGLE_TRANS_COOKIE, { domain: getRootHost(), path: '/' });
}
